<template src="./multiSelectModal.pug" lang="pug"></template>

<script>
import BetterModal from '../betterModal/BetterModal'
import ButtonPrimary from '../../buttons/buttonPrimary/ButtonPrimary'

export default {
  name: 'MultiSelectModal',

  components: {
    BetterModal,
    ButtonPrimary
  },

  props: {
    id: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    dynamicColumns: {
      type: Array,
      default: () => []
    },
    objProperties: {
      type: Object,
      default: () => {
        return {
          id: 'id',
          name: 'nome'
        }
      }
    },
    groups: {
      type: Array,
      default: () => []
    },
    groupingOptions: {
      type: Object
    }
  },

  data () {
    return {
      isEveryItemSelected: this.data.selectedItems.length === 0,
      initialSelection: [...this.data.selectedItems],
      selectedItems: [...this.data.selectedItems],
      hasComputedComponentFunctions: false,
      dataView: this.data.values,
      currentSavedDynamicColumns: [...this.dynamicColumns.map(dynamic => dynamic.column)],
      grouped: false,
      isServerRequest: false,
      showInactiveAccounts: this.data.considerInactiveAccounts
    }
  },

  watch: {
    dynamicColumns: {
      handler () {
        this.removeDynamicColumns()
        for (const dynamic of this.dynamicColumns) {
          this.data.columns.push(dynamic.column)
          this.data.options.sortable.push(dynamic.column)
          this.data.options.headings[dynamic.column] = dynamic.heading
          this.data.options.templates[dynamic.column] = dynamic.template
          this.currentSavedDynamicColumns.push(dynamic.column)
        }
      },
      deep: true
    },
    'data.values': {
      handler () {
        if (!this.data.options.requestFunction) {
          return
        }
        this.updateDataItemsState()
      },
      deep: true
    },
    selectedItems: {
      handler () {
        if (this.groups.length > 1 && this.groupingOptions && this.selectedItems.length === this.data.values.length) {
          this.selectedItems = []
        }
      },
      deep: true
    }
  },

  methods: {
    groupTheData () {
      if (this.groups.length <= 1) {
        return
      }
      const groupingHash = {}

      const groupByProp = this.groupingOptions.prop.split('.')
      const groupIdProp = this.groupingOptions.groupIdProp
      const groupIdValueFallback = this.groupingOptions.defaultIdGroup

      this.groups.forEach(group => {
        group.dataView = []
        if (!groupingHash[group[groupIdProp]]) {
          groupingHash[group[groupIdProp]] = group
        }
      })
      this.data.values.forEach(data => {
        let groupingIdentifier = data
        for (const prop of groupByProp) {
          groupingIdentifier = groupingIdentifier[prop]
          if (!groupingIdentifier) {
            break
          }
        }
        groupingHash[groupingIdentifier || groupIdValueFallback].dataView.push(data)
      })
    },

    removeDynamicColumns () {
      for (const current of this.currentSavedDynamicColumns) {
        const index = this.data.columns.findIndex(column => column === current)
        if (index === -1) {
          continue
        }
        this.data.columns.splice(index, 1)
        this.data.options.sortable.splice(index, 1)
        delete this.data.options.headings[current]
        delete this.data.options.templates[current]
      }
    },

    defineCheckConditions () {
      if (this.data.columns[0] !== 'check') {
        this.data.columns.unshift('check')
      }

      this.updateDataItemsState()
    },

    updateDataItemsState () {
      const defaultState = !this.data.options.requestFunction
      this.dataView = this.data.values.map(value => {
        const selected = this.selectedItems.find(selected => selected[this.objProperties.id] === value[this.objProperties.id])
        value.checked = (this.selectedItems.length === 0 && defaultState) || !!selected
        return value
      })

      this.isEveryItemSelected = this.data.values.every(value => value.checked)
    },

    toggleAllItens () {
      this.isEveryItemSelected = !this.isEveryItemSelected
      this.dataView = this.dataView.map(value => {
        value.checked = this.isEveryItemSelected
        const index = this.selectedItems.findIndex(selected => selected[this.objProperties.id] === value[this.objProperties.id])
        if (index > -1 && !this.isEveryItemSelected) {
          this.selectedItems.splice(index, 1)
        } else if (index === -1 && this.isEveryItemSelected && this.isServerRequest) {
          this.selectedItems.push(value)
        }
        return value
      })
      if (this.isEveryItemSelected && !this.isServerRequest) {
        this.selectedItems = []
      }
    },

    toggleAllGroupItens (groupIndex) {
      const group = this.groups[groupIndex]
      group.isEveryItemSelected = !group.isEveryItemSelected

      let ignoreSearchingInSelected = false
      if (!group.isEveryItemSelected && this.selectedItems.length === 0) {
        for (const g of this.groups) {
          if (g.id !== group.id) {
            this.selectedItems.push(...g.dataView)
          }
        }
        ignoreSearchingInSelected = true
      }

      group.dataView = group.dataView.map(value => {
        value.checked = group.isEveryItemSelected

        if (ignoreSearchingInSelected) {
          return value
        }

        const index = this.selectedItems.findIndex(selected => selected[this.objProperties.id] === value[this.objProperties.id])
        if (index > -1 && !group.isEveryItemSelected) {
          this.selectedItems.splice(index, 1)
        } else if (index === -1 && group.isEveryItemSelected) {
          this.selectedItems.push(value)
        }
        return value
      })
      this.$set(this.groups, groupIndex, group)
    },

    toggleGrouped () {
      this.grouped = !this.grouped

      if (this.grouped) {
        this.groups.forEach(group => {
          group.isEveryItemSelected = this.selectedItems === 0 || group.dataView.every(item => item.checked)
        })
      } else {
        this.isEveryItemSelected = this.selectedItems === 0 || this.dataView.every(item => item.checked)
      }
    },

    selectItem (item) {
      const stateSelected = !item.checked
      this.dataView = this.dataView.map(value => {
        if (value[this.objProperties.id] === item[this.objProperties.id]) {
          value.checked = stateSelected
        }
        return value
      })

      if (stateSelected) {
        this.selectedItems.push(item)
      } else {
        const index = this.selectedItems.findIndex(selected => selected[this.objProperties.id] === item[this.objProperties.id])
        if (index > -1) {
          this.selectedItems.splice(index, 1)
        }
      }

      if (this.data.options.requestFunction) {
        this.isEveryItemSelected = false
      } else {
        if (this.selectedItems.length === 0 || this.selectedItems.length !== this.data.values.length) {
          this.selectedItems = this.dataView.filter(value => value.checked)
          this.isEveryItemSelected = false
        } else if (this.selectedItems.length === this.data.values.length) {
          this.selectedItems = []
          this.isEveryItemSelected = true
        }
      }
    },

    selectGroupItem (groupIndex, item) {
      const group = this.groups[groupIndex]
      const stateSelected = !item.checked
      group.dataView = group.dataView.map(value => {
        if (value[this.objProperties.id] === item[this.objProperties.id]) {
          value.checked = stateSelected
        }
        return value
      })

      if (stateSelected) {
        this.selectedItems.push(item)
      } else {
        if (this.selectedItems.length === 0) {
          for (const g of this.groups) {
            for (const i of g.dataView) {
              if (i[this.objProperties.id] !== item[this.objProperties.id]) {
                this.selectedItems.push(i)
              }
            }
          }
        } else {
          const index = this.selectedItems.findIndex(selected => selected[this.objProperties.id] === item[this.objProperties.id])
          if (index > -1) {
            this.selectedItems.splice(index, 1)
          }
        }
      }
      group.isEveryItemSelected = group.dataView.every(i => i.checked)
      this.$set(this.groups, groupIndex, group)
    },

    toggleInactiveAccounts () {
      this.showInactiveAccounts = !this.showInactiveAccounts
      this.showAccountsInactive()
    },

    showAccountsInactive () {
      if (this.showInactiveAccounts) {
        this.dataView = this.data.allBankAccounts
        this.data.values = this.dataView
        if (this.grouped && this.groups.length > 1) {
          this.groups.forEach((group, index) => {
            group.isEveryItemSelected = true
            this.toggleAllGroupItens(index)
          })
        } else {
          this.isEveryItemSelected = true
          this.toggleAllItens()
        }
      } else {
        this.isEveryItemSelected = true
        this.toggleAllItens()
        this.dataView = this.data.allBankAccounts.filter((bankAccount) => {
          return bankAccount.ativo
        })
        this.data.values = this.dataView
      }
      this.groupTheData()
    },

    apply () {
      if (this.data.allBankAccounts) {
        if ((this.selectedItems.length === 0 && this.showInactiveAccounts) || this.data.allBankAccounts.length === this.data.values.length) {
          this.data.considerInactiveAccounts = true
        } else {
          this.data.considerInactiveAccounts = false
        }
      }
      this.data.selectedItems = this.selectedItems
      this.closeModal()
    },

    closeModal () {
      this.$emit('close-modal')
    },

    searchGroupItens (groupIndex) {
      const group = this.groups[groupIndex]

      if (group.selected) {
        group.selected = false
        this.$set(this.groups, groupIndex, group)
        return
      }

      this.groups.forEach(g => {
        this.$set(g, 'selected', false)
      })

      group.selected = true
      group.isEveryItemSelected = group.dataView.every(i => i.checked)

      this.$set(this.groups, groupIndex, group)
    }
  },

  created () {
    this.defineCheckConditions()
    this.groupTheData()
    this.hasComputedComponentFunctions = true
    this.isServerRequest = !!this.data.options.requestFunction
  }
}
</script>

<style scoped>
.filterButton {
  width: 3vw;
  margin: 0px !important;
}

.col-search {
  width: 53.5%;
}

.search-button {
  min-width: 7vw;
}

.checkbox {
  margin-top: 0px;
  margin-bottom: 0px;
}

.func-group-labels {
  color: #515151;
  font-size: 13px !important;
  text-transform: initial !important;
  font-weight: normal !important;
  padding-top: 1px;
}

.select-columns {
  margin-bottom: 15px;
}

.multiselect-modal-input {
  width: 20px !important;
}

.multiselect-modal-input-row {
  margin-left: 5px !important;
  margin-right: 3px !important;
}

.multiselect-modal-input-header {
  margin: 0px 3px -5px 5px !important;
}

.flex-column {
  display: flex;
  flex-direction: column;
}

.group-title {
  background-color: transparent;
  color: #27466D;
  font-weight: bold;
  text-align: left;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 0;
}

.group-card {
  width: 100%;
  margin-bottom: 0;
  border: none;
}

.no-margin {
  margin: 0;
}

.padded {
  padding: 10px;
}

.margin-bottom {
  margin-bottom: 10px;
}

.scrollable {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden
}
</style>



// WEBPACK FOOTER //
// MultiSelectModal.vue?1f3d0480